import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaRegFileAlt } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import Modal from '../../../components/ui/Modal';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import StatusTag from '../../../components/ui/StatusTag';
import Field, { TextInput, TextArea, NativeSelect } from '../../../components/ui/Field';
import { engineService } from '../../../services/engineService';
import { friendlyError } from '../../../services/agentsService';
import { CATEGORIES, LANGUAGES, categoryLabel, draftProblems, fillVariables, languageLabel, reasonText, statusOf, toTemplateName, variableCount } from '../../../utils/whatsappTemplates';

// Plantillas de mensaje de la cuenta de WhatsApp Business del número: los mensajes que el negocio puede enviar fuera de la ventana de
// 24 horas (recordatorios, avisos). Meta revisa cada una antes de aprobarla. Todo pasa por el motor, que guarda el acceso a Meta.

const EMPTY = { label: '', name: '', language: 'es_MX', category: 'UTILITY', header: '', body: '', footer: '', buttons: ['', '', ''], examples: [] };

/** Cómo se verá el mensaje en WhatsApp. */
function Bubble({ header, body, footer, buttons }) {
  return (
    <div className="rounded-2xl bg-[#e7ddd3] dark:bg-[#0b141a] p-4" aria-label="Vista previa del mensaje">
      <div className="max-w-[300px] rounded-xl rounded-tl-sm bg-white dark:bg-[#202c33] px-3 py-2 shadow-sm text-[13.5px] leading-snug text-[#111b21] dark:text-[#e9edef]">
        {header && <p className="font-semibold mb-1 break-words">{header}</p>}
        <p className="whitespace-pre-wrap break-words">{body || <span className="opacity-50">El texto del mensaje aparece aquí.</span>}</p>
        {footer && <p className="mt-1 text-[12px] opacity-60 break-words">{footer}</p>}
      </div>
      {buttons.length > 0 && (
        <div className="max-w-[300px] mt-1 space-y-1">
          {buttons.map((b, i) => <div key={i} className="rounded-xl bg-white dark:bg-[#202c33] py-1.5 text-center text-[13px] font-medium text-[#027eb5] dark:text-[#53bdeb] break-words">{b}</div>)}
        </div>
      )}
    </div>
  );
}

function NewTemplateModal({ open, onClose, conn, onCreated }) {
  const [d, setD] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setD(EMPTY); setTouched(false); setError(''); } }, [open]);

  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const vars = variableCount(d.body);
  const buttons = d.buttons.map((b) => b.trim()).filter(Boolean);
  const draft = { ...d, buttons, examples: d.examples.slice(0, vars) };
  const problems = draftProblems(draft);

  const insertVariable = () => setD((x) => ({ ...x, body: `${x.body}${x.body && !/\s$/.test(x.body) ? ' ' : ''}{{${variableCount(x.body) + 1}}}` }));
  const save = async () => {
    setTouched(true);
    if (problems.length) return;
    setBusy(true); setError('');
    try {
      await engineService.createTemplate(conn.id, { name: d.name, language: d.language, category: d.category, header: d.header, body: d.body, footer: d.footer, buttons, examples: draft.examples });
      onCreated(d.name); onClose();
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} width="max-w-4xl" title="Nueva plantilla de mensaje"
      description="Meta la revisa antes de que se pueda enviar; suele tardar de minutos a unas horas."
      footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="primary" onClick={save} loading={busy}>Enviar a revisión de Meta</Btn></>}>
      <div className="grid gap-6 md:grid-cols-[1fr_300px]">
        <div className="space-y-4 min-w-0">
          <Field label="Nombre" hint={d.name ? <>En Meta se guarda como <code className="font-mono">{d.name}</code>.</> : 'Para reconocerla en la lista, por ejemplo «Recordatorio de cita».'}>
            <TextInput value={d.label} onChange={(e) => setD((x) => ({ ...x, label: e.target.value, name: toTemplateName(e.target.value) }))} maxLength={80} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Idioma">
              <NativeSelect value={d.language} onChange={set('language')}>{LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}</NativeSelect>
            </Field>
            <Field label="Tipo" hint={CATEGORIES.find((c) => c.value === d.category)?.hint}>
              <NativeSelect value={d.category} onChange={set('category')}>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</NativeSelect>
            </Field>
          </div>
          <Field label="Encabezado" optional hint="Una línea en negritas arriba del mensaje.">
            <TextInput value={d.header} onChange={set('header')} maxLength={60} />
          </Field>
          <Field label="Mensaje" hint={<>Usa {'{{1}}'}, {'{{2}}'}… donde va un dato que cambia, como el nombre o la fecha. <button type="button" onClick={insertVariable} className="font-semibold text-fg underline underline-offset-2 hover:text-fg-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fg/40 rounded">Agregar variable</button></>}>
            <TextArea rows={5} value={d.body} onChange={set('body')} maxLength={1024} placeholder="Hola {{1}}, te recordamos tu cita del {{2}}. Responde para confirmar." />
          </Field>
          {vars > 0 && (
            <fieldset className="space-y-2">
              <legend className="text-[13px] font-semibold text-fg">Ejemplos para Meta</legend>
              <p className="text-[12.5px] text-fg-dim">Meta los usa para revisar la plantilla. No se envían a nadie.</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {Array.from({ length: vars }, (_, i) => (
                  <Field key={i} label={`Variable {{${i + 1}}}`}>
                    <TextInput value={d.examples[i] || ''} maxLength={200}
                      onChange={(e) => setD((x) => { const ex = [...x.examples]; ex[i] = e.target.value; return { ...x, examples: ex }; })} />
                  </Field>
                ))}
              </div>
            </fieldset>
          )}
          <Field label="Pie" optional hint="Texto pequeño al final, por ejemplo el nombre del negocio.">
            <TextInput value={d.footer} onChange={set('footer')} maxLength={60} />
          </Field>
          <fieldset className="space-y-2">
            <legend className="text-[13px] font-semibold text-fg">Botones de respuesta <span className="font-normal text-fg-faint">(opcional, hasta 3)</span></legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {d.buttons.map((b, i) => (
                <TextInput key={i} aria-label={`Botón ${i + 1}`} value={b} maxLength={25} placeholder={['Confirmar', 'Cambiar fecha', ''][i]}
                  onChange={(e) => setD((x) => { const bs = [...x.buttons]; bs[i] = e.target.value; return { ...x, buttons: bs }; })} />
              ))}
            </div>
          </fieldset>
          {touched && problems.length > 0 && (
            <ul role="alert" className="list-disc pl-5 text-[13px] text-signal space-y-0.5">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          )}
          {error && <p role="alert" className="text-[13px] text-signal">{error}</p>}
        </div>
        <div className="md:sticky md:top-0 self-start">
          <p className="text-[13px] font-semibold text-fg mb-2">Así se verá</p>
          <Bubble header={d.header.trim()} body={fillVariables(d.body, draft.examples)} footer={d.footer.trim()} buttons={buttons} />
        </div>
      </div>
    </Modal>
  );
}

function TestModal({ template, conn, onClose }) {
  const [to, setTo] = useState('');
  const [params, setParams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => { setTo(''); setParams([]); setError(''); setSent(false); }, [template]);
  if (!template) return null;
  const n = template.variables;
  const send = async () => {
    setBusy(true); setError(''); setSent(false);
    try {
      await engineService.sendTestTemplate(conn.id, { name: template.name, language: template.language, to, params: Array.from({ length: n }, (_, i) => params[i] || '') });
      setSent(true);
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };
  const ready = to.replace(/\D/g, '').length >= 8 && Array.from({ length: n }, (_, i) => (params[i] || '').trim()).every(Boolean);
  return (
    <Modal open onClose={onClose} title="Enviar una prueba" description={`Manda «${template.name}» a un WhatsApp para ver cómo llega.`}
      footer={<><Btn variant="ghost" onClick={onClose}>Cerrar</Btn><Btn variant="primary" onClick={send} loading={busy} disabled={!ready}>Enviar prueba</Btn></>}>
      <div className="space-y-4">
        <Field label="Número de WhatsApp" hint="Con lada del país, por ejemplo 5214431234567.">
          <TextInput inputMode="tel" autoComplete="off" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        {Array.from({ length: n }, (_, i) => (
          <Field key={i} label={`Valor de {{${i + 1}}}`}>
            <TextInput value={params[i] || ''} onChange={(e) => setParams((p) => { const x = [...p]; x[i] = e.target.value; return x; })} />
          </Field>
        ))}
        <Bubble header={template.header} body={fillVariables(template.body, params)} footer={template.footer} buttons={template.buttons} />
        <div role="status" aria-live="polite" className="text-[13px]">
          {sent && <p className="text-live font-semibold">Enviado. Revisa el WhatsApp de ese número.</p>}
          {error && <p className="text-signal">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}

export default function WhatsappTemplates({ conn, readOnly, engineReady }) {
  const [list, setList] = useState([]);
  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setList((await engineService.listTemplates(conn.id)).templates || []); setPhase('ready'); }
    catch (e) { setError(friendlyError(e)); setPhase((p) => (p === 'ready' ? 'ready' : 'error')); }
  }, [conn.id]);
  useEffect(() => { if (engineReady) load(); else setPhase('error'); }, [engineReady, load]);

  const sorted = useMemo(() => [...list].sort((a, b) => a.name.localeCompare(b.name)), [list]);

  return (
    <section aria-label="Plantillas de mensaje" className="my-3 rounded-xl bg-raised/50 ring-1 ring-line p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <FaRegFileAlt aria-hidden className="text-[13px] text-fg-dim" />
        <h4 className="text-[14px] font-semibold text-fg">Plantillas de mensaje</h4>
        {phase === 'ready' && <span className="text-[12px] text-fg-faint">{list.length === 1 ? '1 plantilla' : `${list.length} plantillas`}</span>}
        <div className="ml-auto flex gap-2">
          {phase === 'ready' && <Btn size="sm" variant="ghost" onClick={load}>Actualizar</Btn>}
          {!readOnly && engineReady && <Btn size="sm" variant="quiet" onClick={() => setCreating(true)}>Nueva plantilla</Btn>}
        </div>
      </div>
      <p className="mt-1 text-[12.5px] text-fg-dim">Mensajes aprobados por Meta que el negocio puede enviar aunque la persona no haya escrito en las últimas 24 horas, como recordatorios o avisos.</p>

      <div role="status" aria-live="polite" className="mt-3 text-[13px] text-fg-dim">
        {phase === 'loading' && <p aria-busy="true">Leyendo las plantillas en Meta…</p>}
        {phase === 'error' && (
          <div className="space-y-2">
            <p className="text-signal">{engineReady ? error || 'No se pudieron leer las plantillas.' : 'Falta la dirección del motor en el admin.'}</p>
            {engineReady && <Btn size="sm" variant="quiet" onClick={() => { setPhase('loading'); load(); }}>Volver a intentar</Btn>}
          </div>
        )}
        {notice && <p className="text-live font-semibold mb-2">{notice}</p>}
        {phase === 'ready' && error && <p className="text-signal mb-2">{error}</p>}
        {phase === 'ready' && !list.length && <p>Este número todavía no tiene plantillas. Crea la primera con «Nueva plantilla».</p>}
      </div>

      {phase === 'ready' && sorted.length > 0 && (
        <ul className="mt-1 divide-y divide-line" aria-label="Plantillas">
          {sorted.map((t) => {
            const st = statusOf(t.status);
            return (
              <li key={t.id} className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13px] text-fg break-all">{t.name}</span>
                    <StatusTag tone={st.tone} dot>{st.label}</StatusTag>
                  </div>
                  <p className="mt-0.5 text-[12px] text-fg-faint">{languageLabel(t.language)} · {categoryLabel(t.category)}</p>
                  <p className="mt-1 text-[13px] text-fg-dim line-clamp-2 whitespace-pre-wrap">{t.body}</p>
                  {t.rejected_reason && <p className="mt-1 text-[12.5px] text-signal">{reasonText(t.rejected_reason)}</p>}
                </div>
                {!readOnly && (
                  <div className="flex gap-2 shrink-0">
                    {t.status === 'APPROVED' && <Btn size="sm" variant="quiet" onClick={() => setTesting(t)}>Enviar prueba</Btn>}
                    <Btn size="sm" variant="ghost" onClick={() => setRemoving(t)} aria-label={`Borrar ${t.name}`}>Borrar</Btn>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <NewTemplateModal open={creating} onClose={() => setCreating(false)} conn={conn}
        onCreated={(name) => { setNotice(`«${name}» se envió a revisión de Meta. Su estado cambia aquí cuando Meta responda.`); load(); }} />
      <TestModal template={testing} conn={conn} onClose={() => setTesting(null)} />
      <ConfirmModal open={Boolean(removing)} onClose={() => setRemoving(null)} tone="danger" title="Borrar plantilla" confirmLabel="Borrar"
        onConfirm={async () => { await engineService.deleteTemplate(conn.id, removing).catch((e) => { throw new Error(friendlyError(e)); }); setNotice(''); await load(); }}>
        <p>Se borra «{removing?.name}» de la cuenta de WhatsApp en Meta, en todos sus idiomas. Meta no deja volver a usar ese nombre durante 30 días.</p>
      </ConfirmModal>
    </section>
  );
}
